import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, ResolveFn, Router } from '@angular/router';
import { OccupationDetailFacade } from '@cpd-app/state/occupation-detail/occupation-detail.facade';
import { Observable, race } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';
import { OccupationDetail } from '@cpd-app/core-module/models';
import { FavoriteMilitaryOccupationsFacade } from '@cpd-app/state/favorite-military-occupations/favorite-occupations.facade';
import { GoalFacade } from '@cpd-app/state/goal';
import { jobTitleStorageKey } from '@cpd-app/core-module/services/navigation/navigation.service';
import { occupationKey } from '@cpd-app/state/occupation-detail/occupation-detail.state';

export const occupationDetailsResolver: ResolveFn<OccupationDetail> = (
  route: ActivatedRouteSnapshot
): Observable<OccupationDetail> => {
  const occupationDetailFacade = inject(OccupationDetailFacade);
  const favoriteMilitaryOccupationsFacade = inject(
    FavoriteMilitaryOccupationsFacade
  );
  const goalFacade = inject(GoalFacade);
  const router = inject(Router);
  const onetCode: string = route.params['onetCode'];
  const routeJobId: string = route.params['jobId'] || null;

  /*
   * The server names a sub-occupation from the title the CLIENT passes it, so
   * the name has to survive a reload. Navigation state carries it when the
   * member clicked through; storage carries it otherwise, keyed by jobId so a
   * reload on one sub-occupation cannot inherit another's name.
   */
  const jobTitle: string =
    router.getCurrentNavigation()?.extras?.state?.jobTitle ??
    (routeJobId
      ? localStorage.getItem(jobTitleStorageKey(routeJobId))
      : null) ??
    null;

  /*
   * The jobId always goes through, whether or not we recovered a title.
   *
   * Opening /occupation/:onetCode/sub-occupations/:jobId cold -- a shared link,
   * a bookmark, a hard refresh after storage was cleared -- leaves no title to
   * pass, but the member asked for THAT sub-occupation and silently serving the
   * parent's figures under the parent's name is not the page they asked for.
   * The title fallback is for the heading only.
   */
  occupationDetailFacade.clearOccupationDetail();

  occupationDetailFacade.loadOccupationDetails(onetCode, routeJobId, jobTitle);

  /*
   * MILITARY OCCUPATIONS ARE RECOMMENDED AGAINST THE GOAL, NOT THE PAGE.
   *
   * "Military Occupations You May Like" is a recommendation: a way to the work
   * the member is aiming at without leaving service. It was asked for with
   * whatever occupation happened to be on screen, so browsing three
   * occupations gave three different sets of recommendations and none of them
   * were about the thing the member had actually committed to.
   *
   * The career goal wins when there is one. Without a goal there is nothing to
   * aim at, so the occupation in view is the best available subject and the
   * behavior is unchanged.
   *
   * Read once rather than subscribed: the resolver runs per navigation, which
   * is exactly when this needs re-asking, and a live subscription here would
   * outlive the route.
   */
  goalFacade.goals$.pipe(take(1)).subscribe((goals) => {
    favoriteMilitaryOccupationsFacade.loadSimilarMilitaryOccupations(
      goals?.onetCode || onetCode
    );
  });

  /*
   * Audit 30 Sep. A code with no occupation (a typo, an old link) never
   * produced a detail, so this waited forever and the navigation spinner span
   * with it. A load now records why it produced none, for THIS page (the code
   * and job), and the page renders "not found" or "could not load" instead.
   * Keyed, so a failure left over from another page cannot end this wait.
   */
  const key = occupationKey(onetCode, routeJobId);
  return race(
    occupationDetailFacade.occupationDetail$.pipe(
      filter((detail) => !!detail && detail.onetCode === onetCode),
      take(1)
    ),
    occupationDetailFacade.occupationLoadFailure$.pipe(
      filter((failure) => failure?.key === key),
      take(1),
      map(() => null as unknown as OccupationDetail)
    )
  );
}